import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getAllCategory,
  getDetail,
  updateCategory,
} from "../../redux/slices/categorySlice";
import type { CreateCategoryFormTypes } from "../../types/category/category.form";
import Lottie from "lottie-react";
import imageAnimation from "../../animations/image.json";
import { IoIosCloseCircle } from "react-icons/io";
import { toast } from "react-toastify";
import loadingAnimation from "../../animations/loading.json";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import { getErrorMessage } from "../../utils/errorHandler";
import { validateForm } from "../../utils/validateForm";
import { useFileUpload } from "../../hooks/useFileUpload";
import { API } from "../../configs/endpoints";
import type { Image } from "../../types/common/image";
type Payload = {
  id: string;
  onBack: () => void;
};

export const UpdateCategory = ({ id, onBack }: Payload) => {
  const dispatch = useAppDispatch();
  const { allCategory } = useAppSelector((state) => state.category);
  const { category } = useAppSelector((state) => state.category);

  useEffect(() => {
    dispatch(getAllCategory({ isActive: true }));
    dispatch(getDetail(id));
  }, [dispatch, id]);

  const [categoryData, setCategoryData] = useState<CreateCategoryFormTypes>({
    name: "",
    parentId: null,
    image: null,
    isActive: true,
    sortOrder: 0,
    seo: {
      title: "",
      description: "",
      keywords: [""],
    },
  });

  useEffect(() => {
    if (category) {
      setCategoryData({
        name: category.name,
        parentId: category.parentId?._id || null,
        image: category.image,
        isActive: category.isActive,
        sortOrder: category.sortOrder,
        seo: category.seo,
      });
    }
  }, [category]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name.startsWith("seo.")) {
      const key = name.split(".")[1];
      if (key === "keywords") {
        setCategoryData((prev) => ({
          ...prev,
          seo: {
            ...prev.seo,
            keywords: value
              .split(",")
              .map((k) => k.trim())
              .filter((k) => k !== ""),
          },
        }));
      } else {
        setCategoryData((prev) => ({
          ...prev,
          seo: {
            ...prev.seo,
            [key]: value,
          },
        }));
      }
      return;
    }
    setCategoryData((prev) => ({
      ...prev,
      [name]:
        name === "isActive"
          ? value === "true"
          : name === "sortOrder"
            ? Number(value)
            : name === "parentId"
              ? value === "null"
                ? null
                : value
              : value,
    }));
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading: isChooseImage } = useFileUpload<{
    image: Image;
  }>({
    endpoint: API.category.upload,
    kind: "image",
  });
  const handleChooseImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const result = await upload(file);
    e.target.value = "";
    if (result.ok && result.data?.image) {
      setCategoryData((prev) => ({
        ...prev,
        image: {
          url: result.data!.image.url,
          public_id: result.data!.image.public_id,
          status: "temple",
        },
      }));
    }
  };

  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = validateForm([
      { value: categoryData.name, label: "Tên danh mục" },
      { value: categoryData.image, label: "ảnh danh mục", type: "image" },
    ]);
    if (!valid) return;
    setLoading(true);
    try {
      await dispatch(updateCategory({ id, data: categoryData })).unwrap();
      toast.success("Sửa danh mục sản phẩm thành công");
      onBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 bg-white rounded-lg w-full">
      <h2 className="text-2xl font-bold text-gray-900">Thêm danh mục</h2>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex lg:items-center flex-col lg:flex-row gap-2">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Tên danh mục
            </label>
            <input
              required
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleChange}
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
              placeholder="Nhập tên danh mục"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Chọn danh mục cha
            </label>
            <select
              name="parentId"
              value={String(categoryData.parentId)}
              onChange={handleChange}
              className="p-2 text-sm cursor-pointer focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl"
            >
              <option value="null">---Chọn danh mục cha---</option>
              {allCategory.length > 0 &&
                allCategory.map((cate) => (
                  <option key={cate._id} value={cate._id}>
                    {cate.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm text-gray-700 font-semibold">
              Trạng thái
            </label>
            <select
              name="isActive"
              value={String(categoryData.isActive)}
              onChange={handleChange}
              className="p-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors duration-300 cursor-pointer"
            >
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="block text-sm text-gray-700 font-semibold">
                Độ ưu tiên
              </label>
              <p className="text-xs tracking-tighter text-gray-700">
                (Độ ưu tiên từ 0, 0 là lớn nhất)
              </p>
            </div>
            <input
              required
              type="number"
              name="sortOrder"
              value={categoryData.sortOrder}
              onChange={handleChange}
              className="w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 p-2 rounded-lg placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
              placeholder="0, 1, 2,..."
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Chọn ảnh danh mục
          </label>
          <div className="w-25 h-25">
            <input
              ref={inputRef}
              onChange={handleChooseImage}
              type="file"
              className="hidden"
            />
            {categoryData.image ? (
              <div className="w-full h-full relative border border-gray-200 rounded-xl">
                <img
                  src={categoryData.image.url}
                  alt="Ảnh danh mục"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() =>
                    setCategoryData((prev) => ({
                      ...prev,
                      image: null,
                    }))
                  }
                  className="absolute -top-2 -right-2 text-xl text-red-500 cursor-pointer hover:text-red-600"
                >
                  <IoIosCloseCircle />
                </button>
              </div>
            ) : (
              <div className="w-full h-full">
                {isChooseImage ? (
                  <Lottie animationData={loadingAnimation} loop={true} />
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="w-full h-full border border-gray-200 rounded-xl cursor-pointer"
                  >
                    <Lottie animationData={imageAnimation} loop={true} />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 space-y-2">
            <div className="flex items-center justify-center mt-2">
              <p className="block text-sm text-gray-700 font-semibold">SEO</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-gray-700 font-semibold">
                Tiêu đề
              </label>
              <input
                type="text"
                name="seo.title"
                value={categoryData.seo.title}
                onChange={handleChange}
                className="w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 p-2 rounded-lg placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
                placeholder="Ex. Máy khoan cầm tay"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-gray-700 font-semibold">
                Từ khóa tìm kiếm
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="seo.keywords"
                  value={categoryData.seo.keywords}
                  onChange={handleChange}
                  className="w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 p-2 rounded-lg placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
                  placeholder="Máy khoan"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-gray-700 font-semibold">
                Mô tả
              </label>
              <textarea
                name="seo.description"
                value={categoryData.seo.description}
                onChange={handleChange}
                className="focus:outline-none p-2 focus:border-blue-500 border border-gray-200 rounded-xl placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400 w-full"
                placeholder="Nhập mô tả ngắn"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="">
            <button
              type="button"
              onClick={onBack}
              className="p-2 text-white rounded-ld bg-blue-500 text-sm rounded-lg cursor-pointer hover:bg-blue-600 transition-colors duration-300"
            >
              Quay lại
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setCategoryData({
                  name: "",
                  parentId: null,
                  image: null,
                  isActive: true,
                  sortOrder: 0,
                  seo: {
                    title: "",
                    description: "",
                    keywords: [""],
                  },
                });
              }}
              className="p-2 rounded-lg text-sm text-white bg-red-500 hover:bg-red-600 transition-colors duration-300 cursor-pointer"
            >
              Hủy
            </button>
            {loading ? (
              <Lottie
                animationData={buttonLoadingAnimation}
                loop={true}
                className="h-10 w-20"
              />
            ) : (
              <button
                type="submit"
                className="p-2 rounded-lg text-sm text-white bg-green-500 hover:bg-green-600 transition-colors duration-300 cursor-pointer"
              >
                Sửa danh mục
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
